import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import AutoCompleteUIDTO, { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import useLocalStorage, { STORAGE_KEYS } from "../hooks/useLocalStorage";
import Position from "../models/Position";
import PositionService from "../services/PositionService";
import ProjectService from "../services/ProjectService";
import { QueryKeyFactory } from "../services/queryKeys";
import UserService from "../services/UserService";
import WorkLogService from "../services/WorkLogService";
import { BillingPolicy, SUM_HOURS_WITH_MINIMUM_KEY } from "../types/BillingPolicy";
import { Routes } from "../types/Routes";
import RoleUtils from "../utils/RoleUtils";
import { rethrowWithMessage, unwrap } from "../utils/queryFn";
import { createTableQuery, refreshTableQuery, TABLE_QUERY_STALE_TIME } from "../utils/tableQuery";
import useUpdateUrl from "./useUpdateUrl";

export enum PositionStatus {
  ACTIVE = "Active",
  INACTIVE = "Inactive",
}

const positionKeys = new QueryKeyFactory(PositionService.path);
const projectKeys = new QueryKeyFactory(ProjectService.path);
const userKeys = new QueryKeyFactory(UserService.path);
const workLogKeys = new QueryKeyFactory(WorkLogService.path);
const NO_NAMES: AutoCompleteUIDTO[] = [];
export const ALL_PROJECTS_ID = "ALL";
export const ALL_PROJECTS_OPTION: AutoCompleteUIDTO = { label: "All", externalId: ALL_PROJECTS_ID };
const PROJECT_PARAM = "project";
const ASSIGNED_PARAM = "assigned";
const POSITION_DELETE_BLOCKED_MESSAGE = "Cannot delete because there is an associated assignment.";

function toDeleteError(error: unknown): never {
  return rethrowWithMessage(error, POSITION_DELETE_BLOCKED_MESSAGE);
}

const usePositionList = () => {
  const queryClient = useQueryClient();
  const [canManageProjects, setCanManageProjects] = useState<boolean>(false);
  const [forceUpdateOn, setForceUpdateOn] = useState<number>();
  const [selectedPosition, setSelectedPosition] = useState<Position | undefined>(undefined);
  const [newBillingPolicy, setNewBillingPolicy] = useState<boolean>(false);
  const [assigned, setAssigned] = useLocalStorage<boolean>(STORAGE_KEYS.STORED_ASSIGNED_TO_ME, false);
  const [searchRoleKey, setSearchRoleKey] = useState<AutoCompleteUIDTO>({ externalId: "All", label: "All" });
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [hasWorklogs, setHasWorklogs] = useState<boolean>(false);
  const [bonusValue, setBonusValue] = useState(0);
  const [isShadowChecked, setIsShadowChecked] = useState(false);
  const [selectedProject, setSelectedProject] = useState<AutoCompleteUIDTO | undefined>(undefined);
  const [storedProject, setStoredProject] = useLocalStorage<AutoCompleteUIDTO>(STORAGE_KEYS.STORED_PROJECT, EMPTY_AUTOCOMPLETE);
  const [positionStatus, setPositionStatus] = useState<string>(PositionStatus.ACTIVE);
  const searchParams = new URLSearchParams(useLocation().search);
  const project = searchParams.get(PROJECT_PARAM);
  const role = searchParams.get("role");
  const assignedToMe = searchParams.get(ASSIGNED_PARAM);
  const { updateUrl } = useUpdateUrl(Routes.POSITIONS, searchParams);
  // The positions query matches every project only when this is the literal "ALL". Blank becomes null and matches nothing.
  const projectExternalId = selectedProject?.externalId || "";
  const concreteProjectId = projectExternalId === ALL_PROJECTS_ID ? "" : projectExternalId;
  const roleKey = searchRoleKey.label === "All" ? "" : searchRoleKey.label;
  const activePositions = positionStatus === PositionStatus.ACTIVE;

  const projectsQuery = useQuery({
    queryKey: projectKeys.list({ scope: "position-names", onlyExternal: false, assigned }),
    queryFn: () => unwrap(ProjectService.findAllProjectsNames(false, assigned)),
    staleTime: TABLE_QUERY_STALE_TIME,
  });
  const projectsNames = projectsQuery.data ?? NO_NAMES;

  const usersQuery = useQuery({
    queryKey: userKeys.list({ scope: "names" }),
    queryFn: () => unwrap(UserService.findUserNames()),
    staleTime: TABLE_QUERY_STALE_TIME,
  });

  const projectDetailQuery = useQuery({
    queryKey: projectKeys.detail(concreteProjectId),
    queryFn: () => unwrap(ProjectService.getByExternalId(concreteProjectId)),
    enabled: !!concreteProjectId,
  });

  const managerQuery = useQuery({
    queryKey: projectKeys.detail(`manager:${concreteProjectId}`),
    queryFn: async () => {
      if (RoleUtils.isAdmin()) return true;
      try {
        await unwrap(ProjectService.isUserCoreManagerFromProject(concreteProjectId, AuthService.getUser()?.externalId!));
        return true;
      } catch {
        return false;
      }
    },
    enabled: !!concreteProjectId,
  });

  const onDataFetch = useMemo(
    () =>
      createTableQuery<Position>({
        queryKey: (page, pageSize, sortBy, sortDir) =>
          positionKeys.list({ page, pageSize, sortBy, sortDir, projectExternalId, roleKey, active: activePositions }),
        queryFn: (page, pageSize) =>
          unwrap(PositionService.findByProjectExternalIdAndRoleKey(page, pageSize, projectExternalId, roleKey, activePositions)),
        client: queryClient,
      }),
    [queryClient, projectExternalId, roleKey, activePositions],
  );

  const refreshTable = () => refreshTableQuery(positionKeys.lists(), () => setForceUpdateOn(Date.now()), queryClient);

  const deleteMutation = useMutation({
    mutationFn: async (position: Position) => {
      try {
        return await unwrap(PositionService.delete(position));
      } catch (error) {
        toDeleteError(error);
      }
    },
    onSuccess: async () => {
      setSelectedPosition(undefined);
      await refreshTable();
      NotificationService.info("Position deleted successfully!");
    },
  });

  const createMutation = useMutation({
    mutationFn: (position: Position) => unwrap(PositionService.create(position)),
    onSuccess: async () => {
      await refreshTable();
      NotificationService.info("New position created successfully!");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (position: Position) => unwrap(PositionService.update(position)),
    onSuccess: async () => {
      await refreshTable();
      NotificationService.info("Position updated successfully!");
    },
  });

  useEffect(() => {
    const selectAllProjects = () => {
      setSelectedProject(ALL_PROJECTS_OPTION);
      searchParams.set(PROJECT_PARAM, ALL_PROJECTS_ID);
    };
    const clearProject = () => {
      setSelectedProject(EMPTY_AUTOCOMPLETE);
      searchParams.delete(PROJECT_PARAM);
    };

    if (project === ALL_PROJECTS_ID) {
      if (assigned) clearProject();
      else setSelectedProject(ALL_PROJECTS_OPTION);
    } else if (project && projectsNames.length > 0) {
      const projectFilter = projectsNames.find((p) => p.externalId === project);
      if (projectFilter) setSelectedProject(projectFilter);
      else if (assigned) clearProject();
      else selectAllProjects();
    } else if (storedProject?.externalId === ALL_PROJECTS_ID && projectsNames.length > 0) {
      if (assigned) clearProject();
      else {
        setSelectedProject(ALL_PROJECTS_OPTION);
        searchParams.set(PROJECT_PARAM, ALL_PROJECTS_ID);
      }
    } else if (storedProject?.externalId && projectsNames.length > 0) {
      const projectFilter = projectsNames.find((p) => p.externalId === storedProject.externalId);
      if (projectFilter) {
        setSelectedProject(projectFilter);
        searchParams.set(PROJECT_PARAM, storedProject.externalId);
      } else if (assigned) clearProject();
      else selectAllProjects();
    } else if (!assigned && projectsNames.length > 0) {
      selectAllProjects();
    }

    if (role !== null) {
      setSearchRoleKey({ externalId: role, label: role });
    } else {
      searchParams.set("role", "All");
    }

    if (assignedToMe !== null) {
      setAssigned(assignedToMe === "true");
    } else if (assigned !== undefined) {
      searchParams.set(ASSIGNED_PARAM, assigned.toString());
    }

    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsNames]);

  useEffect(() => {
    if (RoleUtils.isAdmin()) {
      setCanManageProjects(true);
      return;
    }
    setCanManageProjects(!!managerQuery.data);
  }, [managerQuery.data]);

  useEffect(() => {
    const policy = projectDetailQuery.data?.defaultBillingPolicy;
    if (!policy) return;
    setNewBillingPolicy(policy === SUM_HOURS_WITH_MINIMUM_KEY);
  }, [projectDetailQuery.data]);

  const handleOnDeletePosition = () => {
    if (!selectedPosition) return;
    deleteMutation.mutate(selectedPosition);
  };

  const handleOnChangeInputBillingPolicy = (event: any) => {
    const value = event.target.value;
    setNewBillingPolicy(value === SUM_HOURS_WITH_MINIMUM_KEY);
    setBonusValue(0);
  };

  const handleChangeAssignedProjects = () => {
    const nextAssigned = !assigned;
    setAssigned(nextAssigned);
    searchParams.set(ASSIGNED_PARAM, nextAssigned.toString());
    if (nextAssigned) {
      setSelectedProject(EMPTY_AUTOCOMPLETE);
      setStoredProject(EMPTY_AUTOCOMPLETE);
      searchParams.delete(PROJECT_PARAM);
    } else {
      setSelectedProject(ALL_PROJECTS_OPTION);
      setStoredProject(ALL_PROJECTS_OPTION);
      searchParams.set(PROJECT_PARAM, ALL_PROJECTS_ID);
    }
    updateUrl();
  };

  const onCreatePosition = (position: Position) => {
    if (!selectedProject?.externalId || selectedProject.externalId === ALL_PROJECTS_ID) {
      NotificationService.error("You must select a project before creating a Position");
      return;
    }
    position.projectExternalId = selectedProject.externalId;
    setNewBillingPolicy(false);
    return createMutation.mutateAsync(position).then(() => undefined);
  };

  const loadWorklogs = (positionExternalId: string) =>
    queryClient.fetchQuery({
      queryKey: workLogKeys.list({ scope: "by-position", positionExternalId }),
      queryFn: () => unwrap(WorkLogService.findByPositionExternalId(positionExternalId)),
    });

  const onUpdatePosition = async (position: Position): Promise<boolean> => {
    const worklogs = await loadWorklogs(position.externalId!);
    setHasWorklogs(worklogs.length > 0);
    setNewBillingPolicy(position.billingPolicy === BillingPolicy.SUM_HOURS_WITH_MINIMUM);
    await updateMutation.mutateAsync(position);
    return true;
  };

  const onEditPosition = async (position: Position) => {
    const worklogs = await loadWorklogs(position.externalId!);
    setHasWorklogs(worklogs.length > 0);
    setNewBillingPolicy(position.billingPolicy.toString() === SUM_HOURS_WITH_MINIMUM_KEY);
  };

  const onCancelDialog = async () => {
    setHasWorklogs(false);
    setBonusValue(0);
    setIsShadowChecked(false);
    setNewBillingPolicy(false);
    setIsEditing(false);
  };

  const buildBillingTypeText = (instance: Position) => {
    let text = BillingPolicy[instance.billingPolicy];
    if (instance.shadow) {
      text = text + ", Shadow";
    }
    if (instance.onDemand) {
      text = text + ", On Demand";
    }
    if (instance.bonus > 0) {
      text = text + `, Bonus ${instance.bonus}%`;
    }
    return text;
  };

  return {
    buildBillingTypeText,
    onCancelDialog,
    projectsNames,
    canManageProjects,
    forceUpdateOn,
    selectedPosition,
    setSelectedPosition,
    newBillingPolicy,
    setNewBillingPolicy,
    projectsAssigned: NO_NAMES,
    assigned,
    searchRoleKey,
    isLoading: projectsQuery.isFetching || usersQuery.isFetching,
    setSearchRoleKey,
    handleChangeAssignedProjects,
    handleOnChangeInputBillingPolicy,
    handleOnDeletePosition,
    onCreatePosition,
    onUpdatePosition,
    onEditPosition,
    isEditing,
    setIsEditing,
    hasWorklogs,
    userNames: usersQuery.data ?? NO_NAMES,
    isShadowChecked,
    bonusValue,
    setIsShadowChecked,
    setBonusValue,
    searchParams,
    updateUrl,
    selectedProject,
    setSelectedProject,
    positionStatus,
    setPositionStatus,
    storedProject,
    setStoredProject,
    projectDefaultBillingPolicy: projectDetailQuery.data?.defaultBillingPolicy,
    onDataFetch,
  };
};

export default usePositionList;
